/**
 * Settings — Unified Settings Hub (Batch 13A V1.1).
 *
 * Calm role-aware doorway that links to every settings surface the
 * user can actually reach. Deep-links to existing pages — does NOT
 * duplicate any data model, does NOT create new endpoints.
 *
 * Placeholder cards (disabled, coming-soon) are shown for the three
 * V1.2 / V1.3 surfaces that this hub will eventually link to:
 *   • Notification Preferences
 *   • Support / Contact Us
 *   • Payment Confirmation Preferences
 *
 * Doctrine:
 *   • Hub-and-spoke only — every live tile is `<Link to=...>` to an
 *     existing page.
 *   • Three placeholder tiles are render-only — no onClick, no API
 *     wiring. They explain what's coming and where it will live.
 *   • Role visibility is enforced by RoleRoute on the route itself
 *     (allowedRoles = ["learner", "adi", "pdi", "admin"]); each
 *     tile additionally filters its own visibility by role so a
 *     learner doesn't see instructor-only tiles.
 */
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  User,
  Bell,
  MessageSquare,
  CreditCard,
  Heart,
  Sliders,
  TrendingUp,
  Mail,
  HelpCircle,
  LifeBuoy,
  Phone,
  Briefcase,
  ArrowRightLeft,
  Tag,
  Lock,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

// ── tile primitives ───────────────────────────────────────────

const LiveTile = ({ to, icon: Icon, title, blurb, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group relative flex flex-col gap-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 p-5 transition hover:border-slate-400 hover:shadow-md dark:hover:border-slate-500"
  >
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 group-hover:bg-slate-200 dark:group-hover:bg-slate-700">
        <Icon size={20} />
      </div>
      <div className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</div>
    </div>
    <div className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{blurb}</div>
  </Link>
);

const PlaceholderTile = ({ icon: Icon, title, blurb, coming, testid }) => (
  <div
    data-testid={testid}
    className="relative flex flex-col gap-3 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/30 p-5 opacity-80"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
          <Icon size={20} />
        </div>
        <div className="text-base font-semibold text-slate-700 dark:text-slate-300">{title}</div>
      </div>
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400 px-2 py-1 rounded-full bg-slate-200/70 dark:bg-slate-700/60">
        <Lock size={10} /> {coming}
      </span>
    </div>
    <div className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{blurb}</div>
  </div>
);

const Section = ({ title, blurb, children, testid }) => (
  <section data-testid={testid} className="space-y-4">
    <div className="space-y-1">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
      {blurb ? (
        <p className="text-sm text-slate-600 dark:text-slate-400">{blurb}</p>
      ) : null}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {children}
    </div>
  </section>
);

// ── page ──────────────────────────────────────────────────────

export default function Settings() {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const isInstructor = role === "adi" || role === "pdi";
  const isLearner = role === "learner";
  const isAdmin = role === "admin" || user?.is_admin === true;

  const displayName = user?.name || user?.email || "there";

  return (
    <div
      data-testid="settings-hub"
      className="min-h-dvh bg-slate-50 dark:bg-[#0c1120] text-slate-900 dark:text-slate-100"
    >
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-3">
          <Link
            to="/"
            data-testid="settings-back"
            className="inline-flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
          >
            <ArrowLeft size={16} /> Back
          </Link>
          <h1 className="text-xl sm:text-2xl font-semibold ml-2">Settings</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-10">
        <div className="space-y-1">
          <p className="text-base text-slate-700 dark:text-slate-300">
            Hi {displayName} — everything you can configure lives here.
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            One calm doorway. Each tile takes you to the page that owns that setting.
          </p>
        </div>

        {/* ── Account & identity (all roles) ─────────────── */}
        <Section
          title="Account &amp; identity"
          blurb="Verify your email and manage how you appear on the platform."
          testid="settings-section-account"
        >
          <LiveTile to="/settings/chatgpt" icon={MessageSquare} title="ChatGPT connection"
          blurb="Read your NDG diary, conversations and learning context from ChatGPT. Manage or revoke access."
          testid="settings-tile-chatgpt" />
        <LiveTile
            to="/settings/identity"
            icon={User}
            title="Identity"
            blurb="Verify your email address and check your account status."
            testid="settings-tile-identity"
          />
        </Section>

        {/* ── Learner preferences ──────────────────────────── */}
        {isLearner && (
          <Section
            title="Learning preferences"
            blurb="Tune how lessons should feel for you, day to day."
            testid="settings-section-learner"
          >
            <LiveTile
              to="/passport/profile"
              icon={Heart}
              title="Preferences &amp; passport"
              blurb="Your reasonable adjustments, nervous triggers, and learning style."
              testid="settings-tile-preferences"
            />
            <LiveTile
              to="/settings/growth-ladder"
              icon={TrendingUp}
              title="Growth ladder"
              blurb="Where you are on the learner growth journey."
              testid="settings-tile-growth-ladder-learner"
            />
            <LiveTile
              to="/settings/notifications"
              icon={Bell}
              title="Notification preferences"
              blurb="Choose which lesson and booking notifications you receive."
              testid="settings-tile-notification-preferences"
            />
          </Section>
        )}

        {/* ── Instructor / PDI tools ──────────────────────── */}
        {isInstructor && (
          <Section
            title="Pupil messaging &amp; business"
            blurb="How you communicate, share work, and price your lessons."
            testid="settings-section-instructor"
          >
            <LiveTile
              to="/settings/sms"
              icon={MessageSquare}
              title="SMS"
              blurb="Per-trigger templates and Twilio configuration."
              testid="settings-tile-sms"
            />
            <LiveTile
              to="/settings/email-connections"
              icon={Mail}
              title="Email connections"
              blurb="Connect iCloud Mail for secure, read-only correspondence search."
              testid="settings-tile-email-connections"
            />
            <LiveTile
              to="/settings/marketplace"
              icon={Briefcase}
              title="Marketplace"
              blurb="Your listing, offers, and what pupils see when they find you."
              testid="settings-tile-marketplace"
            />
            <LiveTile
              to="/settings/transfer-network"
              icon={ArrowRightLeft}
              title="Transfer network"
              blurb="Who you'll accept transfers from, and your handover preferences."
              testid="settings-tile-transfer-network"
            />
            <LiveTile
              to="/settings/growth-ladder"
              icon={TrendingUp}
              title="Growth ladder"
              blurb="Your progression as an instructor."
              testid="settings-tile-growth-ladder-instructor"
            />
          </Section>
        )}

        {/* ── Admin tools ──────────────────────────────────── */}
        {isAdmin && (
          <Section
            title="Platform &amp; admin"
            blurb="Operator-level controls. Use with care."
            testid="settings-section-admin"
          >
            <LiveTile
              to="/admin/pricing"
              icon={Tag}
              title="Pricing"
              blurb="Default platform pricing and override behaviour."
              testid="settings-tile-admin-pricing"
            />
          </Section>
        )}

        {/* ── Coming soon · V1.2 / V1.3 placeholders ────── */}
        <Section
          title="Coming soon"
          blurb="Three new settings surfaces are being built next."
          testid="settings-section-coming-soon"
        >
          <PlaceholderTile
            icon={Bell}
            title="Notification preferences"
            blurb="Choose which emails, SMS, and in-app alerts you receive. One control surface for every channel."
            coming="V1.2"
            testid="settings-tile-notification-prefs-placeholder"
          />
          <PlaceholderTile
            icon={CreditCard}
            title="Payment confirmation"
            blurb="Decide whether you'd like an SMS when a lesson payment lands. Off by default — opt in when you're ready."
            coming="V1.3"
            testid="settings-tile-payment-confirmation-placeholder"
          />
          <PlaceholderTile
            icon={LifeBuoy}
            title="Support &amp; contact us"
            blurb="Reach the platform team about a billing question, account issue, or anything else."
            coming="V1.3"
            testid="settings-tile-support-placeholder"
          />
        </Section>

        {/* ── Terms & policies · permanently available ───────── */}
        <Section
          title="Terms &amp; policies"
          blurb="Read the rules and permissions that apply to your account at any time."
          testid="settings-section-legal"
        >
          <LiveTile
            to="/terms"
            icon={HelpCircle}
            title="Terms & conditions"
            blurb="Read the current terms. Your acceptance record keeps the version and date you agreed to."
            testid="settings-tile-terms"
          />
        </Section>

        {/* ── Help (always last) ───────────────────────────── */}
        <Section
          title="Help"
          blurb="Find a manual, walk through a feature, or read the latest notes."
          testid="settings-section-help"
        >
          <LiveTile
            to="/help"
            icon={HelpCircle}
            title="Help centre"
            blurb="Manuals, search, and feature walk-throughs."
            testid="settings-tile-help"
          />
        </Section>

        <footer className="pt-6 text-center text-xs text-slate-500 dark:text-slate-500">
          Need something we don&apos;t list yet? It&apos;ll arrive on this page first.
        </footer>
      </main>
    </div>
  );
}

